import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import CreateGroup from "./CreateGroup";
import Title from "./TitleRenderer";

function MyGroups({ showCreateForm, setShowCreateForm }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [matchedUsers, setMatchedUsers] = useState({});
  const [editMode, setEditMode] = useState(false);

  const [inviteInputs, setInviteInputs] = useState({}); // { [groupId]: 'user@example.com' }
  const [inviteStatus, setInviteStatus] = useState({}); // { [groupId]: 'success'|'error'|'' }
  const [inviteMessage, setInviteMessage] = useState({}); // { [groupId]: string }

  const [pendingInvites, setPendingInvites] = useState([]);

  const [leaveGroupPending, setLeaveGroupPending] = useState(null);

  // Helper to get CSRF token from cookies
  function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== "") {
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.startsWith(name + "=")) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  useEffect(() => {
    fetch("/groups/api/")
      .then((res) => {
        if (!res.ok) throw new Error("Error fetching data");
        return res.json();
      })
      .then(async (data) => {
        setGroups(data.groups || []);
        setPendingInvites(data.new_user_invites || []);
        setLoading(false);

        const matches = {};
        for (const group of data.groups || []) {
          try {
            const res = await fetch(`/groups/api/${group.id}/recommendations/`);
            if (res.ok) {
              const result = await res.json();
              matches[group.id] = result?.matched_users || [];
            }
          } catch (e) {
            console.error(`Error fetching matched users for group ${group.id}:`, e);
          }
        }
        setMatchedUsers(matches);
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setLoading(false);
      });
  }, []);

  // Handle clicking outside popup to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editMode && !event.target.closest(".groups-edit-btn") && !event.target.closest(".groups-section")) {
        setEditMode(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editMode]);

  const handleGroupCreated = (newGroup) => {
    setGroups((prev) => [...prev, newGroup]);
    setShowCreateForm(false);
  };

  const handleOptionsClick = () => {
    setEditMode(!editMode);
  };

  const handleEditGroups = () => {
    // TODO: Implement edit groups functionality
    console.log("Edit groups clicked");
    setEditMode(false);
  };

  const handleLeaveGroup = (groupId) => {
    // TODO: Implement leave group functionality
    setLeaveGroupPending(groupId);
    console.log("Leave group clicked for group:", groupId);
  };

  const handleLeaveGroupConfirm = (groupId) => {
    const csrfToken = getCookie("csrftoken");

    fetch(`/groups/api/leave/${groupId}/respond/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      credentials: "same-origin",
    })
      .then((res) => {
        if (res.ok) {
          // Remove group from UI without reloading
          setGroups((prev) => prev.filter((group) => group.id !== groupId));
          setLeaveGroupPending(null);
        } else {
          console.error("Failed to leave group");
          alert("Could not leave group. Please try again.");
        }
      })
      .catch((err) => {
        console.error("Network error leaving group:", err);
        alert("Network error. Please try again.");
      });
  };

  // Handle input change for invite email/username per group
  const handleInviteInputChange = (groupId, value) => {
    setInviteInputs((prev) => ({ ...prev, [groupId]: value }));
    setInviteStatus((prev) => ({ ...prev, [groupId]: "" }));
    setInviteMessage((prev) => ({ ...prev, [groupId]: "" }));
  };

  // Handle sending invite for a group
  const handleSendInvite = (groupId) => {
    const userToInvite = inviteInputs[groupId];
    if (!userToInvite || userToInvite.trim() === "") {
      setInviteStatus((prev) => ({ ...prev, [groupId]: "error" }));
      setInviteMessage((prev) => ({ ...prev, [groupId]: "Please enter a user email or username." }));
      return;
    }

    const csrfToken = getCookie("csrftoken");

    fetch(`/groups/api/${groupId}/invite/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify({ user_lookup: userToInvite }),
    })
      .then(async (res) => {
        if (res.ok) {
          setInviteStatus((prev) => ({ ...prev, [groupId]: "success" }));
          setInviteMessage((prev) => ({ ...prev, [groupId]: "Invite sent successfully!" }));
          setInviteInputs((prev) => ({ ...prev, [groupId]: "" }));

          // Auto-hide success after 3 seconds
          setTimeout(() => {
            setInviteStatus((prev) => ({ ...prev, [groupId]: "" }));
            setInviteMessage((prev) => ({ ...prev, [groupId]: "" }));
          }, 3000);
        } else {
          const errorData = await res.json();
          setInviteStatus((prev) => ({ ...prev, [groupId]: "error" }));
          setInviteMessage((prev) => ({ ...prev, [groupId]: errorData.error || "Failed to send invite." }));
        }
      })
      .catch((err) => {
        console.error("Error sending invite:", err);
        setInviteStatus((prev) => ({ ...prev, [groupId]: "error" }));
        setInviteMessage((prev) => ({ ...prev, [groupId]: "Network error. Please try again." }));
      });
  };

  function sendInviteResponse(inviteId, accepted, onSuccess) {
    const csrfToken = getCookie("csrftoken");

    fetch(`/groups/api/invite/${inviteId}/respond/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      credentials: "same-origin",
      body: JSON.stringify({ response: accepted }),
    })
      .then(async (res) => {
        if (res.ok) {
          // Remove the invite from the list
          setPendingInvites((prev) => prev.filter((invite) => invite.id !== inviteId));

          // Reload the page to update the groups list
          window.location.reload();
        } else {
          const errorData = await res.json().catch(() => ({ detail: "Unknown error" }));
          console.error("Error responding to invite:", errorData);
          alert("Could not send response. Try again.");
        }
      })
      .catch((err) => {
        console.error("Error responding to invite:", err);
        alert("Could not send response. Try again.");
      });
  }

  function removeInviteFromList(id) {
    setPendingInvites((prev) => prev.filter((invite) => invite.id !== id));
  }

  return (
    <div className="groups-container">
      {/* Show CreateGroup form if toggled */}
      {showCreateForm && <CreateGroup onGroupCreated={handleGroupCreated} onCancel={() => setShowCreateForm(false)} />}

      {/* My Invites Section - Now at the top */}
      <div className="invites-section">
        <h2 className="invites-title">My Invites</h2>
        {pendingInvites.length === 0 ? (
          <p className="no-invites">You have no pending invites.</p>
        ) : (
          <div className="invites-list">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="invite-card">
                <p className="invite-text">
                  <strong>{invite.sender_name}</strong> invited you to join <strong>{invite.group_name}</strong>
                </p>
                <div className="invite-actions">

                  <button 
                    className="btn btn-refresh" 
                    onClick={() => sendInviteResponse(invite.id, true)}
                  >
                    Accept
                  </button>
                  <button 
                    className="btn btn-decline" 
                    onClick={() => sendInviteResponse(invite.id, false)}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Groups Section */}
      <div className="groups-section">
        <div className="groups-header">
          <h2 className="groups-title">My Groups</h2>
          <button className="groups-edit-btn" onClick={handleOptionsClick}>
            {editMode ? "Done" : "Edit"}
          </button>
        </div>
        {loading ? (
          <div className="loading-state">
            <p>Loading groups...</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="empty-state">
            <p>You're not part of any groups yet.</p>
          </div>
        ) : (
          <div className="groups-list">
            {groups.map((group) => (
              <div key={group.id} className="group-card" style={{ position: "relative" }}>
                {leaveGroupPending === group.id && (
                  <div className="group-leave-overlay">
                    <div className="group-leave-backdrop" />
                    <div className="group-leave-modal">
                      <p className="group-leave-title">Are you sure you want to leave this group?</p>
                      <div className="group-leave-actions">
                        <button className="btn group-leave-confirm" onClick={() => handleLeaveGroupConfirm(group.id)}>
                          YES, LEAVE
                        </button>
                        <button className="btn group-leave-cancel" onClick={() => setLeaveGroupPending(null)}>
                          CANCEL
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="group-card-content">
                  {/* Left side: Title and Description */}
                  <div className="group-left-section">
                    <div className="group-info">
                      <h2 className="group-name">{group.name}</h2>
                      <p className="group-description">{group.description}</p>
                    </div>
                  </div>

                  {/* Vertical divider */}
                  <div className="group-divider"></div>

                  {/* Right side: Members, Actions, and Invite */}
                  <div className="group-right-section">
                    {/* Top section: Large View Recommendations button */}
                    <div className="group-top-section">
                      <div className="recommendations-button-container">
                        <Link
                          to={`/groups/${group.id}/recommendations`}
                          state={{ groupName: group.name }}
                          className={`btn btn-large-recommendations ${editMode ? "edit-mode" : ""}`}
                        >
                          <span className="btn-text-desktop">View Recommendations</span>
                          <span className="btn-text-mobile">Recommendations</span>
                        </Link>
                        {editMode && (
                          <button className="btn btn-delete-group" onClick={() => handleLeaveGroup(group.id)} aria-label="Leave group">
                            <i className="fas fa-door-closed icon-door-closed" aria-hidden="true"></i>
                            <i className="fas fa-door-open icon-door-open" aria-hidden="true"></i>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Bottom section: Members and Invite */}
                    <div className="group-bottom-section">
                      <div className="members-container">
                        <div className="group-members">
                          <strong>Members:</strong>
                          <br />
                          {group.members.join(", ")}
                          {matchedUsers[group.id] && matchedUsers[group.id].length > 0 && (
                            <div className="matched-users">
                              <strong>Matched Users:</strong> {matchedUsers[group.id].join(", ")}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="invite-container">
                        <div className="invite-input-section">
                          <strong>Send Invite:</strong>
                          <div className="invite-input-row">
                            <input
                              type="text"
                              placeholder="username/email"
                              value={inviteInputs[group.id] || ""}
                              onChange={(e) => handleInviteInputChange(group.id, e.target.value)}
                              className="invite-input"
                              autoComplete="off"
                              spellCheck="false"
                            />
                            <button className="btn btn-share" onClick={() => handleSendInvite(group.id)} title="Send Invite">
                              <i className="fas fa-paper-plane"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {inviteStatus[group.id] && <p className={`invite-status ${inviteStatus[group.id]}`}>{inviteMessage[group.id]}</p>}
                  </div>
                </div>

                {/* Recommendations Box */}
                {expandedGroupId === group.id && (
                  <div className="recommend-box">
                    <Title query="recommendations" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyGroups;
